import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Avatar,
  Paper,
  Divider,
  CircularProgress,
} from "@mui/material";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

const BookTourForm = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const state = location.state || {};

  const urlTripName = searchParams.get("trip");
  const price1 = searchParams.get("p1") || "0.00";
  const price2 = searchParams.get("p2") || "0.00";
  const price3 = searchParams.get("p3") || "0.00";

  console.log("📍 URL Params:", { urlTripName, price1, price2, price3 });

  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    phone: "",
    country: "",
    tour_date: "",
    participants: 1,
    pickup_location: "",
    dropoff_location: "",
    terms_accepted: false,
    type: "",
  });

  const [showInvoiceSection, setShowInvoiceSection] = useState(false);
  const [bookingData, setBookingData] = useState(null);
  const [countries, setCountries] = useState([]);
  const [countryLoading, setCountryLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  const getPricePerPerson = (participants) => {
    if (participants === 1) return parseFloat(price1) || 0;
    if (participants === 2) return parseFloat(price2) || 0;
    return parseFloat(price3) || 0;
  };

  const calculateAmount = () => {
    const participants = formData.participants || 1;
    const pricePerPerson = getPricePerPerson(participants);
    return (pricePerPerson * participants).toFixed(2);
  };

  const getCurrentPricingInfo = () => {
    const participants = formData.participants || 1;
    const pricePerPerson = getPricePerPerson(participants);
    const pricingText =
      participants === 1 ? "Solo Rate" : participants === 2 ? "Couple Rate" : "Group Rate (3+)";
    return { pricePerPerson, pricingText };
  };

  useEffect(() => {
    let typeToSet = "tour";
    if (urlTripName) {
      typeToSet = decodeURIComponent(urlTripName)
        .replace(/-/g, " ")
        .replace(/\b\w/g, (l) => l.toUpperCase());
    } else if (state.tourName) {
      typeToSet = state.tourName;
    }
    setFormData((prev) => ({ ...prev, type: typeToSet }));
  }, [urlTripName, state.tourName]);

  useEffect(() => {
    const fetchCountries = async () => {
      setCountryLoading(true);
      try {
        const response = await fetch("https://eventscons.com/eventbackendnew/api/countries");
        const json = await response.json();

        const list = Array.isArray(json?.data) ? json.data : [];

        const formattedCountries = list
          .map((country) => ({
            id: country.id,
            name: country.name,
            iso2: country.iso2,
            iso3: country.iso3,
            phone_code: country.phone_code,
            flag: country.flag,
            flag_url: country.flag_url,
          }))
          .sort((a, b) => a.name.localeCompare(b.name));

        setCountries(formattedCountries);
      } catch (error) {
        console.error("❌ Error fetching countries:", error);
        setCountries([]);
      } finally {
        setCountryLoading(false);
      }
    };

    fetchCountries();
  }, []);

  const BOOK_TOUR_URL = "https://eventscons.com/eventbackendnew/api/book-tour";

  const getAuthToken = () => {
    if (typeof window !== "undefined") {
      return (
        localStorage.getItem("token") ||
        localStorage.getItem("authToken") ||
        localStorage.getItem("access_token")
      );
    }
    return null;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : name === "participants" ? Number(value) : value,
    }));
  };

  const handleCountryChange = (event, newValue) => {
    setFormData((prev) => ({
      ...prev,
      country: newValue ? newValue.name : "",
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const participants = formData.participants || 1;
    const pricePerPerson = getPricePerPerson(participants);
    const calculatedAmount = (pricePerPerson * participants).toFixed(2);

    const payload = {
      ...formData,
      amount: parseFloat(calculatedAmount),
      price_per_person: pricePerPerson,
    };

    console.log("🚀 API Payload:", payload);
    setApiError("");
    setLoading(true);

    try {
      const token = getAuthToken();
      const res = await fetch(BOOK_TOUR_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errJson = await res.json().catch(() => null);
        throw new Error(errJson?.message || `Failed to book ${formData.type}: ${res.status}`);
      }

      const json = await res.json();
      console.log("✅ BOOK RESPONSE:", json);

      setBookingData({
        ...payload,
        booking_id: json.booking_id || json.id || `BK${Date.now()}`,
        booking_date: new Date().toISOString().split("T")[0],
        pricePerPerson: pricePerPerson.toFixed(2),
        calculatedAmount,
        pricingTier: participants === 1 ? "Solo" : participants === 2 ? "Couple" : "Group",
      });
      setShowInvoiceSection(true);
    } catch (err) {
      console.error("❌ Error booking:", err);
      setApiError(err.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  const handlePayNow = async () => {
    if (!bookingData) return;

    setLoading(true);
    setApiError("");

    try {
      console.log("🚀 Creating payment for amount:", bookingData.amount);

      const paymentResponse = await fetch("https://eventscons.com/eventbackendnew/api/create-payment", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          amount: parseFloat(bookingData.amount),
          return_url: "https://www.google.com",
          cancel_url: "https://www.google.com",
        }),
      });

      if (!paymentResponse.ok) {
        const errorData = await paymentResponse.json().catch(() => null);
        throw new Error(errorData?.message || "Payment creation failed");
      }

      const paymentData = await paymentResponse.json();
      console.log("✅ Payment created:", paymentData);

      if (paymentData.approval_url) {
        window.location.href = paymentData.approval_url;
      } else {
        throw new Error("No approval URL received from payment gateway");
      }
    } catch (error) {
      console.error("❌ Payment error:", error);
      setApiError(error.message || "Payment initiation failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const { pricePerPerson: currentPrice, pricingText } = getCurrentPricingInfo();

  return (
    <>
      <Box
        sx={{
          maxWidth: 600,
          mx: "auto",
          my: 4,
          p: 3,
          marginTop: "130px",
          borderRadius: 2,
          boxShadow: "0 8px 20px rgba(0,0,0,0.1)",
          backgroundColor: "#fff",
        }}
      >
        <Typography
          variant="h5"
          sx={{
            mb: 1,
            fontWeight: 600,
            textAlign: "center",
            color: "#9B1321",
          }}
        >
          {formData.type ? `Book Your ${formData.type}` : "Book Your Tour"}
        </Typography>

        <Typography variant="body2" sx={{ mb: 3, textAlign: "center", color: "#64748b" }}>
          Fill in the details below to book your tour.
        </Typography>

        <Paper sx={{ p: 3, mb: 3, bgcolor: "#f0f8ff" }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6}>
              <Typography variant="body1" color="textSecondary">
                Participants: <strong>{formData.participants}</strong>
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {pricingText}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body1" color="textSecondary">
                Rate per person:
              </Typography>
              <Typography variant="h5" fontWeight={700} color="#9B1321">
                ${currentPrice.toFixed(2)}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h4" sx={{ color: "#9B1321", fontWeight: 700 }}>
                Total: ${calculateAmount()}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, fontSize: "0.9rem" }}>
                ({currentPrice.toFixed(2)}/person × {formData.participants})
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {!showInvoiceSection && (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Full Name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  fullWidth
                  required
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Autocomplete
                  options={countries}
                  loading={countryLoading}
                  getOptionLabel={(option) => option?.name || ""}
                  value={
                    formData.country
                      ? countries.find((c) => c.name === formData.country) || null
                      : null
                  }
                  onChange={handleCountryChange}
                  isOptionEqualToValue={(option, value) => option?.name === value?.name}
                  renderOption={(props, option) => (
                    <Box component="li" {...props}>
                      <Avatar
                        src={option.flag_url}
                        alt={option.name}
                        variant="square"
                        sx={{ width: 24, height: 18, mr: 1 }}
                      />
                      {option.name}
                    </Box>
                  )}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Country"
                      required
                      fullWidth
                      inputProps={{ ...params.inputProps, autoComplete: "new-password" }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {countryLoading ? <CircularProgress color="inherit" size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                  noOptionsText={countryLoading ? "Loading countries..." : "No countries found"}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Tour Date"
                  name="tour_date"
                  type="date"
                  value={formData.tour_date}
                  onChange={handleChange}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Participants"
                  name="participants"
                  type="number"
                  inputProps={{ min: 1 }}
                  value={formData.participants}
                  onChange={handleChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Pickup Location"
                  name="pickup_location"
                  value={formData.pickup_location}
                  onChange={handleChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Dropoff Location"
                  name="dropoff_location"
                  value={formData.dropoff_location}
                  onChange={handleChange}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="terms_accepted"
                      checked={formData.terms_accepted}
                      onChange={handleChange}
                      color="primary"
                    />
                  }
                  label={
                    <span>
                      I agree to the Cancellation and Refund Policy. I confirm that I
                      have read and accepted the terms in case of cancellation or
                      no-show. Link to{" "}
                      <a
                        href="/tour-terms-and-cancellation.pdf"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#9B1321", fontWeight: "700" }}
                      >
                        terms and conditions
                      </a>
                      .
                    </span>
                  }
                />
              </Grid>
            </Grid>

            {apiError && (
              <Typography variant="body2" sx={{ mt: 2, color: "#b91c1c", textAlign: "center" }}>
                {apiError}
              </Typography>
            )}

            <Button
              type="submit"
              variant="contained"
              disabled={loading || !formData.terms_accepted}
              sx={{
                mt: 3,
                width: "100%",
                borderRadius: "999px",
                backgroundColor: "#9B1321",
                textTransform: "none",
                fontWeight: 600,
                py: 1.2,
                "&:hover": { backgroundColor: "#7f0f1a" },
              }}
            >
              {loading ? `Booking ${formData.type}...` : `Book ${formData.type} - $${calculateAmount()}`}
            </Button>
          </form>
        )}

        {showInvoiceSection && bookingData && (
          <Paper elevation={3} sx={{ p: 3, mt: 2, borderRadius: 2, backgroundColor: "#f8fafc" }}>
            <Typography variant="h6" sx={{ mb: 2, textAlign: "center", color: "#9B1321", fontWeight: 600 }}>
              ✅ Booking Confirmed! #{bookingData.booking_id}
            </Typography>

            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  <strong>Customer Details:</strong>
                </Typography>
                <Typography sx={{ fontWeight: 500 }}>{bookingData.full_name}</Typography>
                <Typography variant="body2">{bookingData.email}</Typography>
                <Typography variant="body2">{bookingData.phone}</Typography>
                <Typography variant="body2">{bookingData.country}</Typography>
              </Grid>
              <Grid item xs={6} sx={{ textAlign: "right" }}>
                <Typography variant="body2" color="textSecondary" sx={{ mb: 1 }}>
                  <strong>Booking Info:</strong>
                </Typography>
                <Typography>Tour: {bookingData.type}</Typography>
                <Typography>Date: {bookingData.tour_date}</Typography>
                <Typography>Participants: {bookingData.participants}</Typography>
                <Typography sx={{ mt: 1 }}>Booked: {bookingData.booking_date}</Typography>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Paper sx={{ p: 2, mb: 3, bgcolor: "#f0f8ff" }}>
              <Typography variant="subtitle1" sx={{ color: "#9B1321", mb: 2, fontWeight: 600 }}>
                💰 Pricing Breakdown ({bookingData.pricingTier})
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">
                    Rate per person ({bookingData.participants} {bookingData.participants === 1 ? "person" : "people"})
                  </Typography>
                  <Typography variant="body1" fontWeight={700}>${bookingData.pricePerPerson}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="textSecondary">Quantity</Typography>
                  <Typography variant="body1" fontWeight={700}>{bookingData.participants}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="h6" sx={{ color: "#9B1321", fontWeight: 700 }}>
                    Total Amount: ${bookingData.amount}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                    (${bookingData.pricePerPerson}/person × {bookingData.participants})
                  </Typography>
                </Grid>
              </Grid>
            </Paper>

            <Box sx={{ textAlign: "center", mt: 2 }}>
              <Typography variant="h4" sx={{ color: "#9B1321", mb: 1, fontWeight: 700 }}>
                ${bookingData.amount}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500, color: "#9B1321" }}>
                Total Amount Due
              </Typography>
            </Box>

            {apiError && (
              <Typography variant="body2" sx={{ mt: 2, color: "#b91c1c", textAlign: "center" }}>
                {apiError}
              </Typography>
            )}

            <Button
              variant="contained"
              onClick={handlePayNow}
              disabled={loading}
              fullWidth
              sx={{
                mt: 3,
                borderRadius: "999px",
                backgroundColor: "#0070ba",
                textTransform: "none",
                fontWeight: 700,
                fontSize: "1.1rem",
                py: 1.8,
                color: "white",
                "&:hover": { backgroundColor: "#005ea6" },
                boxShadow: "0 4px 12px rgba(0,112,186,0.3)",
              }}
            >
              {loading ? (
                <>
                  <CircularProgress size={20} sx={{ mr: 1, color: "white" }} />
                  Processing Payment...
                </>
              ) : (
                `💳 Pay Now Securely - $${bookingData.amount}`
              )}
            </Button>

            <Typography variant="caption" sx={{ display: "block", mt: 2, textAlign: "center", color: "#64748b", lineHeight: 1.4 }}>
              You will be redirected to secure payment gateway
            </Typography>

            <Box sx={{ mt: 3, p: 2, bgcolor: "#f0f9ff", borderRadius: 1, textAlign: "center" }}>
              <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 600 }}>
                <strong>Booking ID:</strong> {bookingData.booking_id}
                <br />
                <small>Keep this for your records and payment reference</small>
              </Typography>
            </Box>
          </Paper>
        )}
      </Box>
    </>
  );
};

export default BookTourForm;